// Phase 62 (Plan 62-06) — TanStack Query wrappers for the To Be Invoiced
// billing builder endpoints.
//
// useToBeInvoicedQueue  — cross-project queue (GET /api/invoices/pending)
// useProjectToBeInvoiced — per-project builder data (GET /api/projects/:id/to-be-invoiced)
// usePatchBilledAmount  — inline billedAmount edit (PATCH /api/expense-receipts/:id)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPendingQueue,
  fetchProjectToBeInvoiced,
  fetchUnloggedAssignees,
  patchBilledAmount,
} from '../services/api/toBeInvoiced';
import type { InvoiceHistoryPage } from '../services/api/toBeInvoiced';

// Queue hook — driven by period string (YYYY-MM).
// MKS-205: `contractValid` is part of the query key so toggling the
// "valid for this period" filter refetches instead of reusing the full list.
export function useToBeInvoicedQueue(period: string, contractValid = false) {
  return useQuery({
    queryKey: ['toBeInvoicedQueue', period, contractValid],
    queryFn: () => fetchPendingQueue(period, { contractValid }),
    staleTime: 30_000,
  });
}

// Per-project builder hook — gated by projectId + enabled flag.
// refetchOnMount: 'always' so navigating back to the detail page pulls fresh
// figures without a manual browser refresh (mirrors useProjectUninvoicedHours
// rationale from 2026-05-18 staging UAT).
// MKS-205: `invoiceHistory` is part of the query key — "Show more" flips it to
// 'all' and TanStack Query refetches the uncapped history.
export function useProjectToBeInvoiced(
  projectId: number | undefined,
  period: string,
  enabled = true,
  invoiceHistory: InvoiceHistoryPage = 'recent',
) {
  return useQuery({
    queryKey: ['projectToBeInvoiced', projectId, period, invoiceHistory],
    queryFn: () => fetchProjectToBeInvoiced(projectId!, period, { invoiceHistory }),
    enabled: !!projectId && enabled,
    staleTime: 10_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

// MKS-207 — people assigned in the period who logged nothing. The review page
// already receives this list inline on the builder payload; this hook exists
// for surfaces that want it on its own (ticket D's reminders).
export function useUnloggedAssignees(
  projectId: number | undefined,
  period: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['unloggedAssignees', projectId, period],
    queryFn: () => fetchUnloggedAssignees(projectId!, period),
    enabled: !!projectId && enabled,
    staleTime: 30_000,
  });
}

// Inline billedAmount edit mutation. On success, invalidates the builder
// cache so the External costs block reflects the latest billed totals.
export function usePatchBilledAmount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, billedAmount }: { id: number; billedAmount: number }) =>
      patchBilledAmount(id, billedAmount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced'] });
    },
  });
}
